"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { toast } from "sonner";

/**
 * 전역 분석 완료 알림 — 분석 시작 후 다른 페이지로 이동해도
 * SSE로 완료를 감지하여 toast + 브라우저 알림을 띄움
 */
export function AnalysisNotifier() {
  const pathname = usePathname();
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    // /result 페이지에서는 이미 자체 SSE를 사용하므로 스킵
    if (pathname?.startsWith("/result")) return;

    // localStorage에서 진행 중인 분석 정보 확인
    const checkPending = () => {
      const pending = localStorage.getItem("analysis-pending");
      if (!pending) return;

      try {
        const { jobId, ticker, startedAt } = JSON.parse(pending);
        // 10분 이상 된 것은 무시
        if (Date.now() - startedAt > 10 * 60 * 1000) {
          localStorage.removeItem("analysis-pending");
          return;
        }

        // SSE 연결
        const baseUrl = process.env.NEXT_PUBLIC_API_URL || "";
        const es = new EventSource(`${baseUrl}/api/ticker/stream/${jobId}`);
        sourceRef.current = es;

        es.addEventListener("complete", (e) => {
          localStorage.removeItem("analysis-pending");
          es.close();

          const displayName = ticker || "종목";

          // Toast 알림
          toast.success(`${displayName} 분석이 완료되었습니다`, {
            description: "클릭하여 결과를 확인하세요",
            duration: 10000,
            action: {
              label: "결과 보기",
              onClick: () => {
                // ticker 가 있으면 /analyze/[ticker] (ReportSection inline), 없으면 /result fallback
                const target = ticker
                  ? `/analyze/${encodeURIComponent(ticker)}`
                  : `/bite/result?id=${jobId}`;
                window.location.href = target;
              },
            },
          });

          // 브라우저 Notification (권한 있을 때만)
          if (Notification.permission === "granted") {
            const n = new Notification("불스리서치", {
              body: `${displayName} 분석이 완료되었습니다`,
              icon: "/favicon.ico",
            });
            n.onclick = () => {
              window.focus();
              const target = ticker
                ? `/analyze/${encodeURIComponent(ticker)}`
                : `/bite/result?id=${jobId}`;
              window.location.href = target;
            };
          }
        });

        es.addEventListener("error", () => {
          // 연결 실패 시 조용히 정리
          es.close();
        });
      } catch {
        localStorage.removeItem("analysis-pending");
      }
    };

    checkPending();

    return () => {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
    };
  }, [pathname]);

  // 첫 방문 시 알림 권한 요청
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      // 사용자가 분석을 시작할 때 권한 요청하도록 localStorage 플래그 설정
      // (여기서 바로 요청하면 UX가 안 좋으므로)
    }
  }, []);

  return null;
}
